// src/pages/TimesheetPage.jsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import api from '../api/api';
import './css/InventoryPage.css';
import TopNav from '../components/TopNav';
import ConfirmModal from '../components/ConfirmModal';
import AlertModal from '../components/AlertModal';
import SignatureCanvas from 'react-signature-canvas';
import { useAuth } from '../context/AuthContext' ;
import useBusinessAddress from '../hooks/useBusinessAddress';
import { businessAddressTemplateValues } from '../utils/businessAddress';

/* =========================
   Template render helpers
   ========================= */

// React renderer (on-screen)
function renderTemplate(str, vars, { isFooter = false } = {}) {
  let replaced = (str || '').replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? ' '));
  replaced = replaced.replace(/\s+([.,;:!?])/g, '$1'); // trim space before punctuation

  const paragraphs = isFooter ? replaced.split(/\n/) : replaced.split(/\n\s*\n/);

  const renderInline = (text) =>
    text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
      /^\*\*[^*]+\*\*$/.test(part)
        ? <strong key={i}>{part.slice(2, -2)}</strong>
        : <span key={i}>{part}</span>
    );

  return paragraphs
    .map(p => p.trim())
    .filter(Boolean)
    .map((p, i) => <p key={i}>{renderInline(p)}</p>);
}

// Print header (React)
function renderHeaderPrintHeader(str) {
  const lines = String(str || '')
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean);

  const line1 = lines[0] || '';
  const line2 = lines[1] || '';

  return (
    <div className="print-head">
      <div className="brand-block">
        <strong>{line1}</strong>
        {line2 ? <span>{line2}</span> : null}
      </div>
    </div>
  );
}

// Pure-string renderers (used to save exact HTML snapshot)
function renderTemplateToHTML(str, vars, { isFooter = false } = {}) {
  let replaced = (str || '').replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? ' '));
  replaced = replaced.replace(/\s+([.,;:!?])/g, '$1');

  const parts = (isFooter ? replaced.split(/\n/) : replaced.split(/\n\s*\n/))
    .map(s => s.trim())
    .filter(Boolean);

  const inline = (text) =>
    text.split(/(\*\*[^*]+\*\*)/g).map((seg) =>
      /^\*\*[^*]+\*\*$/.test(seg) ? `<strong>${seg.slice(2,-2)}</strong>` : seg
    ).join('');

  return parts.map(p => `<p>${inline(p)}</p>`).join('');
}

function renderHeaderToHTML(str) {
  const lines = String(str || '').split('\n').map(s => s.trim()).filter(Boolean);
  const line1 = lines[0] || '';
  const line2 = lines[1] || '';
  return `
    <div class="print-head">
      <div class="brand-block">
        <strong>${line1}</strong>
        ${line2 ? `<span>${line2}</span>` : ''}
      </div>
    </div>
  `;
}

/* =========================
   Keys / default vars
   ========================= */

function timesheetKeyForType(t) {
  if (t === 'sub_contractor') return 'timesheet_sub_contractor';
  if (t?.startsWith('1099')) return t === '1099_part_time' ? 'timesheet_1099_part_time' : 'timesheet_1099_full_time';
  return t === 'part_time' ? 'timesheet_part_time' : 'timesheet_full_time';
}

const safe = (v) => (v === null || v === undefined || v === '' ? ' ' : String(v));

const HEADER_KEY = 'timesheet_header';
const FOOTER_KEY = 'timesheet_footer';

/* =========================
   Confirmation block
   ========================= */

function ConfirmationBlock({
  u,
  weekEndISO,
  daysForWeek,
  hoursForWeek,
  formatLabel,
  formatDate,
  formCache,
  ensureFormLoaded
}) {
  const key = timesheetKeyForType(u.type);

  useEffect(() => { ensureFormLoaded(key); }, [key, ensureFormLoaded]);

  const tmpl = formCache[key]?.content || '';
  const data = {
    name: u.name,
    employer: safe(u?.employer),
    weekEnding: formatDate(weekEndISO) || '____-__-__',
    days: daysForWeek(u.id),
    hours: hoursForWeek(u.id),
    empType: formatLabel(u.type),
  };

  return (
    <div className="row employee-confirmation">
      {tmpl ? renderTemplate(tmpl, data) : null}
    </div>
  );
}

/* =========================
   Page component
   ========================= */

const TimesheetPage = () => {
  const businessAddress = useBusinessAddress();
  const companyVars = {
    brand: 'IRENES',
    brand_sub: 'NYC',
    ...businessAddressTemplateValues(businessAddress || {}),
  };
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [updateEmployee, setUpdateEmployee] = useState(null);

  // NEW: status filter
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'signed' | 'draft'

  const [typeFilter, setTypeFilter] = useState('all');
  const [form, setForm] = useState({ name: '' });
  const { user } = useAuth();

  // per-card fields
  const sigPads = useRef({});                         // { [employeeId]: SignatureCanvasRef }
  const [signDateMap, setSignDateMap] = useState({}); // { [employeeId]: 'YYYY-MM-DD' }
  const [daysMap, setDaysMap] = useState({});         // { [employeeId]: number } (local edits)
  const [hoursMap, setHoursMap] = useState({});       // { [employeeId]: number } (local edits)
  const [savingMap, setSavingMap] = useState({});     // show saving state per card
  const [savedMap, setSavedMap] = useState({});       // small saved toast per card
  const [tsByEmployee, setTsByEmployee] = useState({}); // { [id]: { days, hours, status, signatureSrc?, signedAt?, confirmationHtml? } }

  // Print modal (multi-sheet)
  const [isPrintOpen, setIsPrintOpen] = useState(false);
  const [printSheets, setPrintSheets] = useState([]); // Array of sheets

  // confirm / alerts
  const [confirmState, setConfirmState] = useState({ open: false, message: '', onConfirm: () => {} });
  const [alertState, setAlertState] = useState({ open: false, message: '' });
  const showAlert = (message) => setAlertState({ open: true, message });
  const askConfirm = (message, onConfirm) => {
    setConfirmState({
      open: true,
      message,
      onConfirm: () => { onConfirm(); setConfirmState({ open: false, message: '', onConfirm: () => {} }); },
    });
  };

  // Forms cache
  const [formCache, setFormCache] = useState({}); // { key: formRow }
  const inflight = useRef(new Set());
  const ensureFormLoaded = useCallback(async (key) => {
    if (!key || formCache[key] || inflight.current.has(key)) return;
    try {
      inflight.current.add(key);
      const { data } = await api.get(`/forms/${encodeURIComponent(key)}`);
      if (data) setFormCache((m) => ({ ...m, [key]: data }));
    } catch (e) {
      console.error('Failed to load form', key, e);
    } finally {
      inflight.current.delete(key);
    }
  }, [formCache]);

  // Preload header/footer ASAP so cards/print won't flash placeholders
  useEffect(() => {
    ensureFormLoaded(HEADER_KEY);
    ensureFormLoaded(FOOTER_KEY);
  }, [ensureFormLoaded]);

  // week picker (Sun -> Thu)
  const [weekStartISO, setWeekStartISO] = useState(''); // Sunday
  const [weekEndISO, setWeekEndISO] = useState('');     // Thursday

  useEffect(() => {
    document.title = "Timesheets - Services Irene's Wigs.com";
    // initialize to *this* week's Sun→Thu
    const today = new Date();
    const sun = getSundayStart(today);
    const thu = getThursdayEnd(sun);
    setWeekStartISO(toISOLocal(sun));
    setWeekEndISO(toISOLocal(thu));
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!weekStartISO || !weekEndISO) return;
    loadTimesheetsForRange(weekStartISO, weekEndISO);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStartISO, weekEndISO]);

  /* =========================
     Helpers
     ========================= */

  const formatSignedDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d)) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    if (typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      const [y, m, d] = iso.split('-');
      return `${m}/${d}/${y}`;
    }
    const dt = new Date(iso);
    if (isNaN(dt)) return '';
    const mm = String(dt.getMonth() + 1).padStart(2,'0');
    const dd = String(dt.getDate()).padStart(2,'0');
    const yyyy = dt.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const empDefaultDays = (id) => Number(employees.find(e => e.id === id)?.days ?? 0);
  const empDefaultHours = (id) => Number(employees.find(e => e.id === id)?.hours ?? 0);

  const daysForWeek = (id) => {
    const ts = tsByEmployee[id];
    const { isSigned } = tsStateFor(ts);
    const val = isSigned ? Number(ts?.days ?? 0) : Number(daysMap[id] ?? empDefaultDays(id));
    return Number.isFinite(val) ? val : 0;
  };
  const hoursForWeek = (id) => {
    const ts = tsByEmployee[id];
    const { isSigned } = tsStateFor(ts);
    const val = isSigned ? Number(ts?.hours ?? 0) : Number(hoursMap[id] ?? empDefaultHours(id));
    return Number.isFinite(val) ? val : 0;
  };

  const buildPrintSheet = (u) => {
    const ts = tsByEmployee[u.id];
    if (!ts || !ts.signatureSrc) return null;
    return {
      name: u.name,
      empType: formatLabel(u.type),
      type: u.type,
      employer: safe(u?.employer),
      weekEnding: formatDate(weekEndISO),
      days: Number(ts.days ?? daysForWeek(u.id)),
      hours: Number(ts.hours ?? hoursForWeek(u.id)),
      signatureSrc: ts.signatureSrc,
      signedOn: ts.signedAt ? formatSignedDate(ts.signedAt) : '',
      templateKey: timesheetKeyForType(u.type),
      confirmationHtml: ts.confirmationHtml || null, // header+body only
    };
  };

  const openPrintModalFor = (() => {
    let inFlight = false;
    return async (u) => {
      if (inFlight) return;
      inFlight = true;

      const sheet = buildPrintSheet(u);
      if (!sheet) { showAlert('Timesheet must be signed before printing.'); inFlight = false; return; }

      // Preload in case we need fallbacks
      await Promise.all([
        ensureFormLoaded(sheet.templateKey),
        ensureFormLoaded(HEADER_KEY),
        ensureFormLoaded(FOOTER_KEY),
      ]);

      setPrintSheets([sheet]);
      setIsPrintOpen(true);
      setTimeout(() => { inFlight = false; }, 300);
    };
  })();

  const openPrintModalAllSigned = async () => {
    let baseList = filteredEmployees === null ? employees : filteredEmployees;
    baseList = uniqueBy(baseList, u => u.id);

    let signed = baseList
      .filter(u => {
        const ts = tsByEmployee[u.id];
        return ts && (ts.status === 'signed' || !!ts.signatureSrc);
      })
      .map(buildPrintSheet)
      .filter(Boolean);

    signed = uniqueBy(signed, s => `${s.name}|${s.weekEnding}`);

    if (signed.length === 0) { showAlert('No signed timesheets to print for this week.'); return; }

    const keys = Array.from(new Set(signed.map(s => s.templateKey)));
    keys.push(HEADER_KEY, FOOTER_KEY);
    await Promise.all(keys.map(k => ensureFormLoaded(k)));

    setPrintSheets(signed);
    setIsPrintOpen(true);
  };

  const closePrintModal = () => { setIsPrintOpen(false); setPrintSheets([]); };
  const printModalContent = () => { window.print(); };
  const uniqueBy = (arr, keyFn) => {
    const map = new Map();
    for (const item of arr) map.set(keyFn(item), item);
    return Array.from(map.values());
  };

  /* =========================
     Data loads
     ========================= */

  const loadTimesheetsForRange = async (startISO, endISO) => {
    try {
      const { data } = await api.get('/timesheets', { params: { start: startISO, end: endISO } });

      const byEmp = {};
      (data || []).forEach(r => {
        const rawSig = r.signature_data ?? r.signature ?? r.signature_url ?? null;
        const signatureSrc = normalizeSignature(rawSig);
        const signed = r.status === 'signed' || !!signatureSrc;
        const confirmationHtml = r.confirmation_html || null; // header+body only

        byEmp[r.employee_id] = {
          days: Number(r.days ?? 0),
          hours: Number(r.hours ?? 0),
          status: signed ? 'signed' : (r.status || 'draft'),
          signatureSrc,
          signedAt: r.signed_at || null,
          confirmationHtml,
        };
      });
      setTsByEmployee(byEmp);

      // seed maps from server result for this week
      const ids = (employees || []).map(u => u.id);

      setDaysMap(m => {
        const copy = { ...m };
        ids.forEach(id => {
          const rec = byEmp[id];
          const isSigned = rec?.status === 'signed' || !!rec?.signatureSrc;
          if (isSigned) {
            copy[id] = Number(rec?.days ?? 0); // lock to signed
          } else if (!Number.isFinite(Number(copy[id]))) {
            copy[id] = empDefaultDays(id);
          }
        });
        return copy;
      });

      setHoursMap(m => {
        const copy = { ...m };
        ids.forEach(id => {
          const rec = byEmp[id];
          const isSigned = rec?.status === 'signed' || !!rec?.signatureSrc;
          if (isSigned) {
            copy[id] = Number(rec?.hours ?? 0); // lock to signed
          } else if (!Number.isFinite(Number(copy[id]))) {
            copy[id] = empDefaultHours(id);
          }
        });
        return copy;
      });

      setSignDateMap(m => {
        const copy = { ...m };
        ids.forEach(id => { copy[id] = endISO; }); // display “week ending” (Thu)
        return copy;
      });

    } catch (err) {
      console.error('Failed to load timesheets for range:', err);
      showAlert('Failed to load timesheets for the selected week.');
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await api.get('/employees');
      setEmployees(res.data);

      const todayISO = new Date().toISOString().slice(0, 10);
      const newDates = { ...signDateMap };
      const newDays  = { ...daysMap };
      const newHours = { ...hoursMap };

      res.data.forEach(u => {
        if (newDates[u.id] === undefined) newDates[u.id] = todayISO;

        const d = Number(u.days);
        const h = Number(u.hours);

        if (newDays[u.id]  === undefined) newDays[u.id]  = Number.isFinite(d) ? d : 0;
        if (newHours[u.id] === undefined) newHours[u.id] = Number.isFinite(h) ? h : 0;
      });

      setSignDateMap(newDates);
      setDaysMap(newDays);
      setHoursMap(newHours);
    } catch (err) {
      console.error('Failed to load employees:', err);
    }
  };

  /* =========================
     Interactions
     ========================= */

  const handleemployeeClick = (e, employee) => {
    if (
      e.target.tagName === 'SELECT' ||
      e.target.tagName === 'INPUT' ||
      e.target.tagName === 'TEXTAREA' ||
      e.target.closest('select') ||
      e.target.closest('input') ||
      e.target.closest('button')
    ) return;

    setSelectedEmployee(employee);
    setUpdateEmployee({ ...employee });
  };

  const handleSave = async () => {
    if (!updateEmployee || !selectedEmployee) return;
    try {
      const updatePayload = {
        name: updateEmployee.name,
        type: updateEmployee.type,
        employer: updateEmployee.employer,
        hours: updateEmployee.hours,
      };
      await api.put(`/employees/${selectedEmployee.id}`, updatePayload);
      showAlert('Employee updated.');
      await loadEmployees();
    } catch (err) {
      console.error('Failed to update employee:', err);
      showAlert('Failed to update employee.');
    }
  };

  const handleDelete = async (u) => {
    if (!u || !weekStartISO || !weekEndISO) {
      showAlert('Select an employee and week first.');
      return;
    }
    const ts = tsByEmployee[u.id];
    const { isSigned } = tsStateFor(ts);
    if (!isSigned) {
      showAlert('Timesheet must be signed before it can be deleted.');
      return;
    }

    askConfirm(`Delete timesheet for ${u.name} (week ending ${weekEndISO})?`, async () => {
      try {
        await api.delete(`/timesheets/employees/${u.id}`, {
          params: { start: weekStartISO, end: weekEndISO }
        });

        setTsByEmployee(prev => {
          const copy = { ...prev };
          delete copy[u.id];
          return copy;
        });

        setSavedMap(m => ({ ...m, [u.id]: false }));
        showAlert('Timesheet deleted.');
      } catch (err) {
        console.error('Error deleting timesheet:', err);
        showAlert('Failed to delete timesheet.');
      }
    });
  };

  const handleNameChange = (e) => setForm(prev => ({ ...prev, name: e.target.value }));
  const clearFilter = () => {
    setFilteredEmployees(null);
    setSelectedEmployee(null);
    setUpdateEmployee(null);
    setForm(prev => ({ ...prev, name: '' }));
    setTypeFilter('all');
    setStatusFilter('all');
  };
  const reapplyFilter = (list, name) => {
    const q = name.toLowerCase();
    const exact = list.find(u => (u.name || '').toLowerCase() === q);
    if (exact) { setSelectedEmployee(exact); setUpdateEmployee({ ...exact }); setFilteredEmployees([exact]); return; }
    const partials = list.filter(u => (u.name || '').toLowerCase().includes(q));
    if (partials.length === 1) { setSelectedEmployee(partials[0]); setUpdateEmployee({ ...partials[0] }); setFilteredEmployees(partials); return; }
    if (partials.length > 1) { setSelectedEmployee(null); setUpdateEmployee(null); setFilteredEmployees(partials); return; }
    setSelectedEmployee(null); setUpdateEmployee(null); setFilteredEmployees([]); showAlert('No employee found with that name.');
  };
  const findEmployee = () => {
    const empName = form.name?.trim();
    if (!empName) return showAlert('Please enter a name to search.');
    reapplyFilter(employees, empName);
  };

  const clearSig = (id) => sigPads.current[id]?.clear();

  const saveSig = async (u, e) => {
    e?.stopPropagation?.();
    const pad = sigPads.current[u.id];
    if (!pad || pad.isEmpty()) return showAlert('Please add a signature first.');
    if (!weekStartISO || !weekEndISO) return showAlert('Please select a week first.');

    try {
      setSavingMap(m => ({ ...m, [u.id]: true }));

      const signature = pad.getCanvas().toDataURL('image/png');
      const newDays  = Number(daysForWeek(u.id));
      const newHours = Number(hoursForWeek(u.id));

      // Ensure templates present to compose snapshot
      const bodyKey = timesheetKeyForType(u.type);
      await Promise.all([
        ensureFormLoaded(bodyKey),
        ensureFormLoaded(HEADER_KEY),
        ensureFormLoaded(FOOTER_KEY),
      ]);

      const headerTmpl = formCache[HEADER_KEY]?.content || '';
      const bodyTmpl   = formCache[bodyKey]?.content   || '';

      const bodyVars = {
        name: u.name,
        employer: safe(u?.employer),
        weekEnding: formatDate(weekEndISO) || '____-__-__',
        days: newDays,
        hours: newHours,
        empType: formatLabel(u.type),
      };

      const headerHTML = renderHeaderToHTML(headerTmpl);
      const bodyHTML   = renderTemplateToHTML(bodyTmpl, bodyVars);

      // Save header + body only (footer is rendered after signature)
      const confirmation_html = `${headerHTML}
        <div class="row employee-confirmation" style="margin-top:12px">${bodyHTML}</div>`;

      await api.post(`/timesheets/employees/${u.id}/sign`, {
        week_start: weekStartISO,
        week_end: weekEndISO,
        days: newDays,
        hours: newHours,
        signature,
        confirmation_html,
      });

      // optimistic update
      setTsByEmployee(prev => ({
        ...prev,
        [u.id]: {
          days: newDays,
          hours: newHours,
          status: 'signed',
          signatureSrc: signature,
          signature_data: signature.split(',')[1],
          signedAt: new Date().toISOString(),
          confirmationHtml: confirmation_html, // header+body only
        },
      }));
      setDaysMap(m => ({ ...m, [u.id]: newDays }));
      setHoursMap(m => ({ ...m, [u.id]: newHours }));

      pad.clear();
      await loadTimesheetsForRange(weekStartISO, weekEndISO);

      setSavedMap(m => ({ ...m, [u.id]: true }));
      setTimeout(() => setSavedMap(m => ({ ...m, [u.id]: false })), 2000);
    } catch (err) {
      console.error('Failed to save signature:', err);
      showAlert(err.response?.data?.error || 'Failed to save signature.');
    } finally {
      setSavingMap(m => ({ ...m, [u.id]: false }));
    }
  };

  // date helpers
  const toISOLocal = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  const getSundayStart = (d) => { const dt = new Date(d); const day = dt.getDay(); dt.setDate(dt.getDate() - day); dt.setHours(0,0,0,0); return dt; };
  const getThursdayEnd = (sunday) => { const dt = new Date(sunday); dt.setDate(dt.getDate() + 4); dt.setHours(23,59,59,999); return dt; };

  const getWeekFromPickedDate = (pickedISO) => {
    const picked = new Date(`${pickedISO}T00:00:00`);
    const sun = getSundayStart(picked);
    const thu = getThursdayEnd(sun);
    return { startISO: toISOLocal(sun), endISO: toISOLocal(thu) };
  };

  const handleWeekEndPick = (e) => {
    const v = e.target.value; // "YYYY-MM-DD"
    if (!v) return;
    const { startISO, endISO } = getWeekFromPickedDate(v);
    setWeekStartISO(startISO);
    setWeekEndISO(endISO);
  };

  function buildWeekOptions(count = 12) {
    const opts = [];
    const now = new Date();
    let curSun = getSundayStart(now);
    for (let i = 0; i < count; i++) {
      const start = new Date(curSun);
      const end = getThursdayEnd(start);
      opts.push({
        startISO: toISOLocal(start),
        endISO:   toISOLocal(end),
        label:
          start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
          ' – ' +
          end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
          ' (Sun–Thu)'
      });
      curSun = new Date(start);
      curSun.setDate(curSun.getDate() - 7);
    }
    return opts;
  }

  const tsStateFor = (ts) => {
    const status = ts?.status || 'draft';
    const signatureSrc = ts?.signatureSrc ?? normalizeSignature(ts?.signature_data);
    const isSigned = status === 'signed' || !!signatureSrc;
    return { isSigned, signatureSrc, status };
  };

  function normalizeSignature(sig) {
    if (!sig) return null;
    if (typeof sig === 'string' && sig.startsWith('data:image/')) return sig;
    if (typeof sig === 'string') return `data:image/png;base64,${sig}`;
    if (sig && sig.type === 'Buffer' && Array.isArray(sig.data)) {
      const bytes = new Uint8Array(sig.data);
      let bin = '';
      for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
      return `data:image/png;base64,${btoa(bin)}`;
    }
    return null;
  }

  const formatLabel = (str) => {
    if (!str) return '';
    const parts = str.split('_');
    const formatted = parts.map((part) =>
      part.charAt(0).toUpperCase() + part.slice(1)
    ).join(' ');
    return (parts.length > 2 ? formatted.replace(' ', ' - ') : formatted);
  };

  /* =========================
     Filtering
     ========================= */

  // base list (either full list or search-filtered list)
  let listToRender = filteredEmployees === null ? employees : filteredEmployees;

  // filter by employee type
  if (typeFilter !== 'all') listToRender = listToRender.filter(u => u.type === typeFilter);

  // NEW: filter by status (signed/draft/all)
  if (statusFilter !== 'all') {
    listToRender = listToRender.filter(u => {
      const ts = tsByEmployee[u.id];
      const { isSigned } = tsStateFor(ts);
      return statusFilter === 'signed' ? isSigned : !isSigned; // 'draft' means not signed (includes no record)
    });
  }

  const hasAnySignedThisWeek = (filteredEmployees === null ? employees : filteredEmployees)
    .some(u => {
      const ts = tsByEmployee[u.id];
      return ts && (ts.status === 'signed' || !!ts.signatureSrc);
    });

  /* =========================
     Render
     ========================= */

  return (
    <>
      <TopNav />
      <div className="inventory-container">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Timesheets
          {user?.role === 'admin' && (
            <a href="/forms" style={{ color: 'inherit' }} title="Manage Templates">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 
                  2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 
                  1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 
                  1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 
                  2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 
                  0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 
                  2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 
                  1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 
                  2 0 1 1 2.83-2.83l.06.06a1.65 
                  1.65 0 0 0 1.82.33h.09A1.65 
                  1.65 0 0 0 9 3.09V3a2 
                  2 0 0 1 4 0v.09a1.65 
                  1.65 0 0 0 1 1.51 
                  1.65 1.65 0 0 0 1.82-.33l.06-.06a2 
                  2 0 1 1 2.83 2.83l-.06.06a1.65 
                  1.65 0 0 0-.33 1.82v.09a1.65 
                  1.65 0 0 0 1.51 1H21a2 
                  2 0 0 1 0 4h-.09a1.65 
                  1.65 0 0 0-1.51 1z"/>
              </svg>
            </a>
          )}
        </h2>

        {/* Search by name */}
        <form onSubmit={(e) => { e.preventDefault(); findEmployee(); }}>
          <div className="form-group">
            <div className="input-button-group">
              <input
                type="text"
                placeholder="Employee Name"
                value={form.name || ''}
                onChange={handleNameChange}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); findEmployee(); } }}
                className="input"
              />
              <button type="button" onClick={findEmployee} className="button small blue">Find</button>
              <button type="button" onClick={clearFilter} className="button small yellow">Clear</button>
            </div>
          </div>
        </form>

        {/* Week picker */}
        <div className="form-group">
          <label>Week Ending (Thu):</label>
          <input
            type="date"
            className="input"
            value={weekEndISO}
            onChange={handleWeekEndPick}
          />
          <div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>
            Selected Week: Sun {formatDate(weekStartISO)} → Thu {formatDate(weekEndISO)}
          </div>
        </div>

        {/* Type filter */}
        <div className="filters-row">
          <div className="form-group compact">
          
            <label>Filter by Type:</label>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="input">
              <option value="all">All</option>
              <option value="full_time">Full Time</option>
              <option value="part_time">Part Time</option>
              <option value="1099_full_time">1099 - Full Time</option>
              <option value="1099_part_time">1099 - Part Time</option>
              <option value="sub_contractor">Sub Contractor</option>
            </select>
          </div>

          {/* NEW: Status filter */}
          <div className="form-group compact">
            <label>Filter by Status:</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input">
              <option value="all">All</option>
              <option value="signed">Signed</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>

        {/* Batch print */}
        {hasAnySignedThisWeek && (
          <div>
            <button
              type="button"
              className="button small green"
              onClick={openPrintModalAllSigned}
            >
              Print All Signed (This Week)
            </button>
          </div>
        )}

        <div className="card-list employees">
          {listToRender.length === 0 && <div className="empty-state">No employees to display.</div>}

          {listToRender.map(u => (
            <div key={u.id} className={`card ${selectedEmployee?.id === u.id ? 'selected' : ''}`}>
              <div onClick={(e) => handleemployeeClick(e, u)} style={{ cursor: 'pointer' }}>
                {/* Header with badge */}
                {(() => {
                  const ts = tsByEmployee[u.id];
                  const { isSigned, status } = tsStateFor(ts);
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <h2 style={{ margin: 0 }}>{u.name} ({formatLabel(u.type)})</h2>
                      <span className={`badge ${isSigned ? 'badge-success' : 'badge-neutral'}`}>
                        {isSigned ? 'Signed' : (status || 'Draft')}
                      </span>
                    </div>
                  );
                })()}

                {selectedEmployee?.id === u.id && updateEmployee && (
                  <div className="employee-detail-panel inventory-form" onClick={(e) => e.stopPropagation()}>
                    {(() => {
                      const ts = tsByEmployee[u.id];
                      const { isSigned } = tsStateFor(ts);
                      const dispDays  = daysForWeek(u.id);
                      const dispHours = hoursForWeek(u.id);

                      return (
                        <>
                          {/* Row 1 */}
                          <div
                            className="grid-3 ts-row"
                            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}
                          >
                            <div className="ts-week">
                              <b>Week Ending</b>
                              <p>{formatDate(weekEndISO)}</p>
                            </div>

                            {isSigned ? (
                              <>
                                <div>
                                  <b>Days</b>
                                  <p style={{ marginTop: 6 }}>{dispDays}</p>
                                </div>
                                <div>
                                  <b>Hours</b>
                                  <p style={{ marginTop: 6 }}>{dispHours}</p>
                                </div>
                              </>
                            ) : (
                              <>
                                <div>
                                  <b>Days</b>
                                  <input
                                    type="number"
                                    min={0}
                                    max={7}
                                    className="input"
                                    value={dispDays}
                                    onChange={(e) =>
                                      setDaysMap((m) => ({ ...m, [u.id]: Number(e.target.value) }))} />
                                </div>
                                <div>
                                  <b>Hours</b>
                                  <input
                                    type="number"
                                    min={0}
                                    className="input"
                                    value={dispHours}
                                    onChange={(e) =>
                                      setHoursMap((m) => ({ ...m, [u.id]: Number(e.target.value) }))} />
                                </div>
                              </>
                            )}
                          </div>

                          {/* Row 2: saved confirmation if signed, else live template */}
                          {(() => {
                            const ts = tsByEmployee[u.id];
                            const { isSigned } = tsStateFor(ts);
                            if (isSigned && ts?.confirmationHtml) {
                              return (
                                <div
                                  className="row employee-confirmation"
                                  dangerouslySetInnerHTML={{ __html: ts.confirmationHtml }} // header+body only
                                />
                              );
                            }
                            return (
                              <ConfirmationBlock
                                u={u}
                                weekEndISO={weekEndISO}
                                daysForWeek={daysForWeek}
                                hoursForWeek={hoursForWeek}
                                formatLabel={formatLabel}
                                formatDate={formatDate}
                                formCache={formCache}
                                ensureFormLoaded={ensureFormLoaded}
                              />
                            );
                          })()}
                        </>
                      );
                    })()}

                    {/* Row 3: Signature, THEN Footer (footer always below signature) */}
                    <div className="row employee-signature" onClick={(e) => e.stopPropagation()}>
                      <div className="sig-label">
                        {(u.type==='full_time' || u.type==='part_time') ? 'Employee Signature:' : 'Signature:'}
                      </div>

                      {tsByEmployee[u.id]?.signatureSrc ? (
                        <div style={{ margin: '8px 0' }}>
                          <img
                            src={tsByEmployee[u.id].signatureSrc}
                            alt="Signature"
                            style={{ border: '1px solid #ddd', borderRadius: 6, maxWidth: 320 }}
                          />
                          {tsByEmployee[u.id]?.signedAt && (
                            <div style={{ fontSize: '0.85rem', color: '#555', marginTop: 6 }}>
                              Date: <strong>{formatDate(tsByEmployee[u.id].signedAt)}</strong>
                            </div>
                          )}

                          {/* Footer BELOW signature */}
                          <div className="timesheet-footer" style={{ marginTop: 16 }}>
                            {(() => {
                              const tmpl = formCache[FOOTER_KEY]?.content;
                              return tmpl ? renderTemplate(tmpl, companyVars, { isFooter: true }) : null;
                            })()}
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="sig-box">
                            <SignatureCanvas
                              ref={(ref) => { sigPads.current[u.id] = ref; }}
                              penColor="black"
                              canvasProps={{ width: 400, height: 120, className: 'sigCanvas' }}
                            />
                          </div>
                          <div className="sig-actions">
                            <button
                              type="button"
                              className="button small"
                              onClick={(e) => { e.stopPropagation(); clearSig(u.id); }}
                            >
                              Clear
                            </button>
                            <button
                              type="button"
                              className="button small blue"
                              onClick={(e) => saveSig(u, e)}
                              disabled={!!savingMap[u.id]}
                            >
                              {savingMap[u.id] ? 'Saving…' : 'Save Signature'}
                            </button>
                            {savedMap[u.id] && <span className="sig-saved">✅ Saved</span>}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {selectedEmployee?.id === u.id && (
                <div className="employee-detail-panel">
                  <div className="employee-controls">
                    {(() => {
                      const ts = tsByEmployee[u.id];
                      const { isSigned } = tsStateFor(ts);
                      if (!isSigned) return null;
                      return (
                        <>
                          {(user.role === 'admin' || user.role === 'payroll_manager') && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDelete(selectedEmployee); }}
                              className="button small red"
                            >
                              Delete Timesheet
                            </button>
                          )}

                          <button
                            type="button"
                            className="button small green"
                            onClick={(e) => { e.stopPropagation(); openPrintModalFor(u); }}
                          >
                            Print
                          </button>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {alertState.open && (
        <AlertModal message={alertState.message} onClose={() => setAlertState({ open: false, message: '' })} />
      )}
      {confirmState.open && (
        <ConfirmModal
          open={confirmState.open}
          message={confirmState.message}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState({ open: false, message: '', onConfirm: () => {} })}
        />
      )}

      {isPrintOpen && printSheets.length > 0 && (
        <div className="print-modal-overlay" onClick={closePrintModal}>
          <div className="print-modal" onClick={(e) => e.stopPropagation()}>
            <div className="print-modal-header">
              <h3>Print Preview</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="button small carmine" onClick={closePrintModal}>Close</button>
              </div>
            </div>

            <div className={`print-sheet-wrap ${printSheets.length === 1 ? 'single' : ''}`}>
              {printSheets.map((s, i) => (
                <div className="print-sheet" key={i}>
                  {/* Use saved confirmation HTML when present (header+body only) */}
                  {s.confirmationHtml ? (
                    <>
                      <div dangerouslySetInnerHTML={{ __html: s.confirmationHtml }} />

                      {/* Signature */}
                      <div className="sig" style={{ marginTop: 16 }}>
                        <div className="label">
                          {(s.type === 'full_time' || s.type === 'part_time') ? 'Employee Signature' : 'Signature'}
                        </div>
                        <img src={s.signatureSrc} alt="Signature" />
                        {s.signedOn && <div className="foot">Date: <strong>{s.signedOn}</strong></div>}
                      </div>

                      {/* Footer BELOW signature */}
                      <div className="timesheet-footer" style={{ marginTop: 16 }}>
                        {(() => {
                          const tmpl = formCache[FOOTER_KEY]?.content;
                          return tmpl ? renderTemplate(tmpl, companyVars, { isFooter: true }) : null;
                        })()}
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Fallback header */}
                      {(() => {
                        const tmpl = formCache[HEADER_KEY]?.content;
                        return tmpl ? renderHeaderPrintHeader(tmpl) : null;
                      })()}

                      {/* Fallback body */}
                      <div className="row employee-confirmation" style={{ marginTop: 12 }}>
                        {(() => {
                          const tmpl = formCache[s.templateKey]?.content || '';
                          const vars = {
                            name: s.name,
                            employer: safe(s.employer),
                            weekEnding: s.weekEnding,
                            days: s.days,
                            hours: s.hours,
                            empType: s.empType,
                          };
                          return tmpl ? renderTemplate(tmpl, vars) : null;
                        })()}
                      </div>

                      {/* Signature */}
                      <div className="sig" style={{ marginTop: 16 }}>
                        <div className="label">
                          {(s.type === 'full_time' || s.type === 'part_time') ? 'Employee Signature' : 'Signature'}
                        </div>
                        <img src={s.signatureSrc} alt="Signature" />
                        {s.signedOn && <div className="foot">Date: <strong>{s.signedOn}</strong></div>}
                      </div>

                      {/* Footer BELOW signature */}
                      <div className="timesheet-footer" style={{ marginTop: 16 }}>
                        {(() => {
                          const tmpl = formCache[FOOTER_KEY]?.content;
                          return tmpl ? renderTemplate(tmpl, companyVars, { isFooter: true }) : null;
                        })()}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            <div className="print-modal-actions no-print">
              <button className="button small blue" onClick={printModalContent}>Print</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TimesheetPage;
